import type { Page } from './types';

const DATABASE_NAME = 'agentico-reliability';
const DATABASE_VERSION = 1;
const STORE_NAME = 'page-drafts';
const FALLBACK_KEY = 'agentico.page-drafts.v1';

export interface PageDraftInput {
  workspaceId: string;
  page: Page;
  baseVersion?: number;
}

export interface PageDraft {
  pageId: string;
  workspaceId: string;
  mutationId: string;
  baseVersion: number;
  createdAt: string;
  updatedAt: string;
  page: Page;
}

const memoryFallback = new Map<string, PageDraft>();
let databasePromise: Promise<IDBDatabase> | null = null;
let indexedDbUnavailable = false;

function mutationId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function isPageDraft(value: unknown): value is PageDraft {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Partial<PageDraft>;
  return typeof record.pageId === 'string'
    && typeof record.workspaceId === 'string'
    && typeof record.mutationId === 'string'
    && typeof record.baseVersion === 'number'
    && typeof record.createdAt === 'string'
    && typeof record.updatedAt === 'string'
    && typeof record.page === 'object'
    && record.page !== null
    && record.page.id === record.pageId;
}

function fallbackRead(): Map<string, PageDraft> {
  try {
    const raw = localStorage.getItem(FALLBACK_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        const records = parsed.filter(isPageDraft);
        memoryFallback.clear();
        for (const record of records) memoryFallback.set(record.pageId, record);
      }
    }
  } catch {
    // Private browsing and storage pressure can make localStorage unavailable.
  }
  return memoryFallback;
}

function fallbackWrite(records: Map<string, PageDraft>): void {
  memoryFallback.clear();
  for (const [key, value] of records) memoryFallback.set(key, value);
  try {
    localStorage.setItem(FALLBACK_KEY, JSON.stringify([...records.values()]));
  } catch {
    // The in-memory copy remains usable for the current app lifetime.
  }
}

function openDatabase(): Promise<IDBDatabase> {
  if (indexedDbUnavailable || !('indexedDB' in globalThis)) return Promise.reject(new Error('IndexedDB is unavailable'));
  if (databasePromise) return databasePromise;
  const pending = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'pageId' });
        store.createIndex('workspaceId', 'workspaceId', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => database.close();
      resolve(database);
    };
    request.onerror = () => reject(request.error ?? new Error('Could not open the draft database'));
    request.onblocked = () => reject(new Error('Draft database upgrade was blocked'));
  }).catch((error: unknown): never => {
    indexedDbUnavailable = true;
    databasePromise = null;
    throw error;
  });
  databasePromise = pending;
  return pending;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Draft database request failed'));
  });
}

async function withStore<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  const transaction = database.transaction(STORE_NAME, mode);
  const result = await requestResult(operation(transaction.objectStore(STORE_NAME)));
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Draft database transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Draft database transaction was aborted'));
  });
  return result;
}

export async function putPageDraft(input: PageDraftInput): Promise<PageDraft> {
  const timestamp = new Date().toISOString();
  const existing = await getPageDraft(input.page.id);
  const draft: PageDraft = {
    pageId: input.page.id,
    workspaceId: input.workspaceId,
    mutationId: mutationId(),
    baseVersion: input.baseVersion ?? input.page.version ?? existing?.baseVersion ?? 1,
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
    page: structuredClone(input.page),
  };
  try {
    await withStore('readwrite', (store) => store.put(draft));
  } catch {
    const records = fallbackRead();
    records.set(draft.pageId, draft);
    fallbackWrite(records);
  }
  return draft;
}

export async function getPageDraft(pageId: string): Promise<PageDraft | null> {
  try {
    const value = await withStore('readonly', (store) => store.get(pageId));
    return isPageDraft(value) ? value : null;
  } catch {
    return fallbackRead().get(pageId) ?? null;
  }
}

export async function listPageDrafts(workspaceId?: string): Promise<PageDraft[]> {
  let records: PageDraft[];
  try {
    const values = workspaceId
      ? await withStore('readonly', (store) => store.index('workspaceId').getAll(workspaceId))
      : await withStore('readonly', (store) => store.getAll());
    records = values.filter(isPageDraft);
  } catch {
    records = [...fallbackRead().values()].filter((draft) => !workspaceId || draft.workspaceId === workspaceId);
  }
  return records.sort((left, right) => left.updatedAt.localeCompare(right.updatedAt));
}

export async function removePageDraft(pageId: string, acknowledgedMutationId?: string): Promise<boolean> {
  const existing = await getPageDraft(pageId);
  if (!existing || (acknowledgedMutationId && existing.mutationId !== acknowledgedMutationId)) return false;
  try {
    await withStore('readwrite', (store) => store.delete(pageId));
  } catch {
    const records = fallbackRead();
    records.delete(pageId);
    fallbackWrite(records);
  }
  return true;
}

export async function clearPageDrafts(workspaceId?: string): Promise<void> {
  if (!workspaceId) {
    try {
      await withStore('readwrite', (store) => store.clear());
    } catch {
      fallbackWrite(new Map());
    }
    return;
  }
  const drafts = await listPageDrafts(workspaceId);
  await Promise.all(drafts.map((draft) => removePageDraft(draft.pageId, draft.mutationId)));
}
